import React, { useState, useEffect } from "react";
import CommonFormActions from "../../components/common/CommonFormActions";
import MainLayout from "../../components/layout/MainLayout";
import PageLoader from "../../components/common/PageLoader";
import CommonTable from "../../components/common/CommonTable";
import api from "../../services/api";
import Swal from "sweetalert2";
import "./Attendance.css";

function MarkAttendance() {
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [dateFrom, setDateFrom] = useState(getTodayDate());
  const [dateTo, setDateTo] = useState(getTodayDate());
  const [attendanceData, setAttendanceData] = useState({});
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [bulkTimeIn, setBulkTimeIn] = useState("");
  const [bulkTimeOut, setBulkTimeOut] = useState("");

  function getTodayDate() {
    return new Date().toISOString().split("T")[0];
  }

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await api.get("/attendance/mark");

      if (response.data) {
        setEmployees(response.data.users || []);
      }
    } catch (error) {
      console.error("Failed to fetch employees:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load employee data",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeesByDate = async (date) => {
    try {
      setLoading(true);
      const response = await api.post("/attendance/by-date", { date });

      if (response.data) {
        setEmployees(response.data.users || []);
        setAttendanceData({}); // Reset attendance data
      }
    } catch (error) {
      console.error("Failed to fetch employees by date:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateFromChange = (e) => {
    const newDate = e.target.value;
    setDateFrom(newDate);
    fetchEmployeesByDate(newDate);
  };

  const handleTimeChange = (userId, field, value) => {
    setAttendanceData((prev) => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [field]: value,
      },
    }));
  };

  const getHolidayLabel = (employee) => {
    if (!employee?.holiday_titles?.length) {
      return "Holiday";
    }

    return employee.holiday_titles.join(", ");
  };

  const handleSubmit = async () => {
    const attendances = Object.entries(attendanceData)
      .filter(([userId, data]) => data.time_in && data.time_out)
      .map(([userId, data]) => ({
        user_id: parseInt(userId),
        time_in: data.time_in,
        time_out: data.time_out,
      }));

    if (attendances.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Data",
        text: "Please mark at least one employee attendance",
      });
      return;
    }

    // ── Validate date range ────────────────────────────────────────────────
    if (!dateFrom || !dateTo) {
      Swal.fire({
        icon: "warning",
        title: "Missing Dates",
        text: "Please select both From and To dates",
      });
      return;
    }

    if (new Date(dateTo) < new Date(dateFrom)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Range",
        text: '"To" date cannot be before "From" date',
      });
      return;
    }

    setSubmitting(true);

    try {
      const response = await api.post("/attendance/mark", {
        date_from: dateFrom, // ← was: date: dateFrom
        date_to: dateTo, // ← new
        attendances: attendances,
      });

      if (response.data.success) {
        // Show already-marked warnings if any
        const alreadyMarked = response.data.already_marked || [];
        const holidaySkipped = response.data.holiday_skipped || [];
        const skipped = response.data.skipped_count || 0;

        let html = `<p>${response.data.message}</p>`;

        if (alreadyMarked.length > 0) {
          const list = alreadyMarked
            .slice(0, 5) // show max 5 to avoid huge popup
            .map((a) => `<li>${a.emp_name} (${a.emp_code}) — ${a.date}</li>`)
            .join("");
          const more =
            alreadyMarked.length > 5
              ? `<li>...and ${alreadyMarked.length - 5} more</li>`
              : "";
          html += `<div style="text-align:left;margin-top:12px"><strong>Already marked</strong><ul style="text-align:left;font-size:13px;margin-top:8px">${list}${more}</ul></div>`;
        }

        if (holidaySkipped.length > 0) {
          const list = holidaySkipped
            .slice(0, 5)
            .map(
              (entry) =>
                `<li>${entry.emp_name} (${entry.emp_code}) - ${entry.date}${
                  entry.holiday_titles?.length
                    ? ` [${entry.holiday_titles.join(", ")}]`
                    : ""
                }</li>`
            )
            .join("");
          const more =
            holidaySkipped.length > 5
              ? `<li>...and ${holidaySkipped.length - 5} more</li>`
              : "";
          html += `<div style="text-align:left;margin-top:12px"><strong>Skipped for holiday</strong><ul style="text-align:left;font-size:13px;margin-top:8px">${list}${more}</ul></div>`;
        }

        Swal.fire({
          icon: skipped > 0 ? "warning" : "success",
          title: skipped > 0 ? "Partial Success" : "Success!",
          html: html,
          confirmButtonColor: "#6557ff",
        });

        // Reset form
        setAttendanceData({});
        fetchEmployees();
      }
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to mark attendance";
      Swal.fire({ icon: "error", title: "Error", text: message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadSample = async () => {
    try {
      const response = await api.get("/attendance/download-sample", {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "EmployeeAttendanceSample.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to download sample file",
      });
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      Swal.fire({
        icon: "warning",
        title: "No File",
        text: "Please select a file to import",
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", importFile);
    formData.append("company_id", localStorage.getItem("company_id"));
    formData.append("from", dateFrom);
    formData.append("to", dateTo);

    try {
      const response = await api.post("/attendance/import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: response.data.message,
          timer: 2000,
          showConfirmButton: false,
        });

        setShowImportModal(false);
        setImportFile(null);
        fetchEmployees();
      }
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to import attendance";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: message,
      });
    }
  };

  const handleBulkFill = (field, value) => {
    if (field === "time_in") setBulkTimeIn(value);
    if (field === "time_out") setBulkTimeOut(value);

    // Fill all employees with this time
    setAttendanceData((prev) => {
      const updated = { ...prev };
      employees.forEach((emp) => {
        if (emp.is_holiday) {
          return;
        }
        updated[emp.id] = {
          ...updated[emp.id],
          [field]: value,
        };
      });
      return updated;
    });
  };

  if (loading) {
    return <MainLayout><PageLoader force copy="Loading employees..." /></MainLayout>;
  }

  const attendanceColumns = [
    {
      header: "Sr. No.",
      cellClassName: "text-center",
      render: (_, index) => index + 1,
    },
    {
      header: "Emp. Name",
      render: (employee) => (
        <div className="attendance-employee-cell">
          <span>{`${employee.first_name} ${employee.last_name}`}</span>
          {employee.is_holiday ? (
            <span
              className="attendance-holiday-pill"
              title={getHolidayLabel(employee)}
            >
              <i className="fas fa-umbrella-beach"></i> {getHolidayLabel(employee)}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      header: "Designation",
      cellClassName: "text-center",
      render: (employee) =>
        employee.designations?.[0]?.designation_name || "N/A",
    },
    {
      header: "In Time",
      render: (employee) => (
        employee.is_holiday ? (
          <span className="holiday-input-lock">Holiday</span>
        ) : (
          <input
            type="time"
            className="form-control time-input"
            value={attendanceData[employee.id]?.time_in || ""}
            onChange={(e) =>
              handleTimeChange(employee.id, "time_in", e.target.value)
            }
          />
        )
      ),
    },
    {
      header: "Out Time",
      render: (employee) => (
        employee.is_holiday ? (
          <span className="holiday-input-lock">Holiday</span>
        ) : (
          <input
            type="time"
            className="form-control time-input"
            value={attendanceData[employee.id]?.time_out || ""}
            onChange={(e) =>
              handleTimeChange(employee.id, "time_out", e.target.value)
            }
          />
        )
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="mark-attendance-page">
        <h1 className="page-title">Attendance</h1>
        <div className="page-header-section">
          <div className="date-filters">
            <div className="filter-group">
              <label>From:</label>
              <input
                type="date"
                className="form-control"
                value={dateFrom}
                max={getTodayDate()}
                onChange={handleDateFromChange}
              />
            </div>
            <div className="filter-group">
              <label>To:</label>
              <input
                type="date"
                className="form-control"
                value={dateTo}
                max={getTodayDate()}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </div>

          <div className="action-buttons">
            <button
              className="btn-import"
              onClick={() => setShowImportModal(true)}
            >
              <i className="fas fa-file-upload"></i> Import Attendance
            </button>
            <button
              className="btn-submit"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>

        <div className="table-card">

          {/* ── Bulk Fill Row ─────────────────────────── */}
          <div className="bulk-fill-bar">
            <span className="bulk-fill-label">
              <i className="fas fa-bolt"></i> Fill All:
            </span>

            <div className="bulk-fill-group">
              <label>In Time</label>
              <input
                type="time"
                className="form-control time-input"
                value={bulkTimeIn}
                onChange={(e) => handleBulkFill("time_in", e.target.value)}
              />
            </div>

            <div className="bulk-fill-group">
              <label>Out Time</label>
              <input
                type="time"
                className="form-control time-input"
                value={bulkTimeOut}
                onChange={(e) => handleBulkFill("time_out", e.target.value)}
              />
            </div>

            {/* Optional: Clear All button */}
            {(bulkTimeIn || bulkTimeOut) && (
              <button
                className="btn-clear-bulk"
                onClick={() => {
                  setBulkTimeIn("");
                  setBulkTimeOut("");
                  setAttendanceData({});
                }}
                title="Clear all times"
              >
                <i className="fas fa-times"></i> Clear All
              </button>
            )}
          </div>

          <CommonTable
            columns={attendanceColumns}
            data={employees}
            tableClassName="attendance-table"
            wrapperClassName="table-responsive"
            noDataClassName="text-center"
            emptyMessage="No employees to mark attendance"
          />
        </div>

      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowImportModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5>Import Attendance</h5>
              <button
                className="btn-close"
                onClick={() => setShowImportModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="import-section">
                <button
                  className="btn-download-sample"
                  onClick={handleDownloadSample}
                >
                  <i className="fas fa-download"></i> Download Sample
                </button>

                <div className="file-upload-section">
                  <label htmlFor="file-upload" className="file-upload-label">
                    <i className="fas fa-cloud-upload-alt"></i>
                    <span>
                      {importFile ? importFile.name : "Choose Excel File"}
                    </span>
                  </label>
                  <input
                    id="file-upload"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setImportFile(e.target.files[0])}
                    style={{ display: "none" }}
                  />
                </div>
              </div>
            </div>

            <CommonFormActions
              className="modal-footer"
              cancelLabel="Close"
              submitLabel="Import"
              onCancel={() => setShowImportModal(false)}
              onSubmit={handleImport}
              submitType="button"
            />
          </div>
        </div>
      )}
    </MainLayout>
  );
}

export default MarkAttendance;
